/**
 * Browser page translators (Google Translate, Edge, Safari) rewrite the DOM
 * under React's feet: they wrap and *merge* adjacent text nodes into `<font>`
 * elements. React keeps references to the original text nodes, so the next
 * update that removes or reorders one throws
 *
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * Uncaught during commit, that reaches the root error boundary and replaces the
 * whole app with the "something went wrong" screen — a translated user loses
 * the entire dashboard, not just the widget React was updating.
 *
 * This makes those two mutations no-ops instead of throws when the node has
 * already been reparented by the translator. It is a containment measure, not a
 * repair: the affected subtree can be left visually stale until its next full
 * re-render. Degrading one widget beats losing the app.
 *
 * The real prevention is at the render site — keep dynamic text in its own
 * element so a translator never merges it with static siblings (see the
 * per-stage counter in `Pipelines/components/KanbanBoard.tsx`). This guard is
 * the net for every site we haven't found yet.
 *
 * Long-standing upstream issue: facebook/react#11538.
 */
export function installTranslationCrashGuard(): void {
  if (typeof Node !== "function" || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      console.warn(
        "[translation-crash-guard] skipped removeChild of a reparented node",
        { child, expectedParent: this },
      );
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn(
        "[translation-crash-guard] skipped insertBefore a reparented reference node",
        { newNode, referenceNode, expectedParent: this },
      );
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
